"use client";

/**
 * Theme sync utilities for persisting next-themes selection to a cookie so the
 * server can read the user's preference for `defaultTheme` on the next render.
 *
 * Client source of truth is next-themes / localStorage (the blocking script
 * paints from it). The cookie is only an SSR mirror — ThemeSync writes
 * theme → cookie, never cookie → localStorage.
 */

import { LEGACY_THEME_COOKIE_NAME, THEME_COOKIE_NAME } from "@workspace/ui/lib/theme-cookie";

function readBrowserCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  const value = match?.[2];
  return value ? decodeURIComponent(value) : null;
}

/**
 * Get theme value from cookie (for client-side use).
 * Server-side should use Next.js cookies() + `readThemeCookie`.
 */
export function getThemeFromCookie(): string | null {
  return readBrowserCookie(THEME_COOKIE_NAME) ?? readBrowserCookie(LEGACY_THEME_COOKIE_NAME);
}

function writeBrowserCookie(name: string, value: string, expires: Date, secureFlag: string): void {
  // biome-ignore lint/suspicious/noDocumentCookie: document.cookie is the supported way to write a SSR-readable theme cookie; the Cookie Store API isn't widely supported yet
  document.cookie = `${name}=${value}; path=/; expires=${expires.toUTCString()}; SameSite=Lax${secureFlag}`;
}

function clearBrowserCookie(name: string, secureFlag: string): void {
  // biome-ignore lint/suspicious/noDocumentCookie: expire the legacy theme cookie after migrating to axp-theme
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secureFlag}`;
}

/**
 * Sync the active theme to the SSR cookie. Scoped to the current host (no
 * domain attribute) since axp doesn't share themes across subdomains.
 */
export function syncLocalStorageToCookie(theme: string | null | undefined): void {
  if (typeof document === "undefined" || !theme) {
    return;
  }

  try {
    const encodedValue = encodeURIComponent(theme);

    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);

    const isSecure = window.location.protocol === "https:";
    const secureFlag = isSecure ? "; Secure" : "";

    writeBrowserCookie(THEME_COOKIE_NAME, encodedValue, expires, secureFlag);

    // Drop the legacy cookie once the new name is written so the two cannot diverge.
    if (readBrowserCookie(LEGACY_THEME_COOKIE_NAME) !== null) {
      clearBrowserCookie(LEGACY_THEME_COOKIE_NAME, secureFlag);
    }
  } catch (error) {
    console.warn("Failed to sync theme to cookie:", error);
  }
}

export { LEGACY_THEME_COOKIE_NAME, THEME_COOKIE_NAME } from "@workspace/ui/lib/theme-cookie";
